import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import i18n, { LANGUAGES } from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Inactivity check interval (every 5 minutes)
const ACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000;
// If no user activity for 1h50m, proactively check session
const INACTIVITY_WARNING_MS = 110 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastActivityRef = useRef(Date.now());
  const activityIntervalRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // The profile's language wins over the one remembered in this browser
      const lang = response.data?.language;
      if (lang && LANGUAGES.some((l) => l.code === lang) && lang !== i18n.resolvedLanguage) {
        i18n.changeLanguage(lang);
      }
      return response.data;
    } catch (error) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Track user activity (throttled)
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Listen for user activity events
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, handleUserActivity, { passive: true }));
    return () => {
      events.forEach(event => window.removeEventListener(event, handleUserActivity));
    };
  }, [handleUserActivity]);

  // Periodically check if session is still valid
  useEffect(() => {
    if (!user) return;

    activityIntervalRef.current = setInterval(async () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      // If user has been inactive for close to 2 hours, verify session
      if (timeSinceActivity > INACTIVITY_WARNING_MS) {
        const result = await checkAuth();
        if (!result) {
          // Session expired on server side, redirect to login
          window.location.href = '/login';
        }
      }
    }, ACTIVITY_CHECK_INTERVAL);

    return () => {
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
      }
    };
  }, [user, checkAuth]);

  const login = (userData) => {
    setUser(userData);
    lastActivityRef.current = Date.now();
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      // Force redirect to login
      window.location.href = '/login';
    }
  };

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    return await checkAuth();
  }, [checkAuth]);

  const stopImpersonation = async () => {
    await axios.post(`${API_URL}/api/auth/impersonate/stop`, {}, { withCredentials: true });
    await checkAuth();
    window.location.href = '/dashboard';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, refreshUser, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};
